import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowDownRight } from "lucide-react";
import { LOGO, HERO_IMG, PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "../lib/site";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.19, 1, 0.22, 1], delay: 0.3 + i * 0.14 },
  }),
};

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className || ""}`}
      variants={lineReveal}
      initial="hidden"
      animate="show"
      custom={i}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen bg-ink text-ivory overflow-hidden flex items-center pt-28 pb-16"
      data-testid="hero-section"
    >
      {/* ambient gold glow */}
      <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-gold/10 blur-[130px]" />
      <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] rounded-full bg-gold/5 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center w-full">
        {/* Left */}
        <motion.div style={{ y: textY }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="flex items-center gap-4 mb-8"
          >
            <img src={LOGO} alt="MB Notary Services logo" className="h-20 w-20 rounded-full ring-1 ring-gold/40 object-cover" data-testid="hero-logo" />
            <div className="h-14 w-px bg-gold/30" />
            <p className="eyebrow text-gold">Illinois Mobile<br />Notary Public</p>
          </motion.div>

          <h1 className="font-serif font-normal leading-[0.95] text-5xl sm:text-6xl lg:text-7xl tracking-tight">
            <Line i={0}>Chicago Mobile</Line>
            <Line i={1}>Notary</Line>
            <Line i={2} className="gold-text italic">Services</Line>
          </h1>

          <div className="overflow-hidden mt-6">
            <motion.p
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1], delay: 0.7 }}
              className="font-serif italic text-taupe text-xl sm:text-2xl"
            >
              Professional. Reliable. Convenient.
            </motion.p>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-6 max-w-md text-ivory/70 font-light leading-relaxed"
          >
            A refined mobile notary experience delivered to your door across
            Chicago and the surrounding suburbs — discreet, precise, and always on your schedule.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a href="#booking" className="btn-gold rounded-full px-8 py-4 text-xs font-medium tracking-widest uppercase inline-flex items-center gap-2" data-testid="hero-book-btn">
              Book Appointment <ArrowDownRight size={16} />
            </a>
            <a href={PHONE_HREF} className="rounded-full px-7 py-4 text-xs font-medium tracking-widest uppercase border border-gold/40 text-gold hover:bg-gold/10 transition-colors" data-testid="hero-call-btn">
              Call Now
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-10 flex flex-col sm:flex-row gap-5 sm:gap-8 text-sm"
          >
            <a href={PHONE_HREF} className="flex items-center gap-3 text-ivory/80 hover:text-gold transition-colors" data-testid="hero-phone">
              <span className="h-9 w-9 rounded-full border border-gold/30 grid place-items-center"><Phone size={15} className="text-gold" strokeWidth={1.5} /></span>
              {PHONE}
            </a>
            <a href={EMAIL_HREF} className="flex items-center gap-3 text-ivory/80 hover:text-gold transition-colors" data-testid="hero-email">
              <span className="h-9 w-9 rounded-full border border-gold/30 grid place-items-center"><Mail size={15} className="text-gold" strokeWidth={1.5} /></span>
              {EMAIL}
            </a>
          </motion.div>
        </motion.div>

        {/* Right image */}
        <motion.div
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1], delay: 0.4 }}
          className="relative hidden lg:block"
        >
          <div className="relative rounded-[1.6rem] overflow-hidden ring-1 ring-gold/25 shadow-2xl h-[34rem]">
            <motion.img
              src={HERO_IMG}
              alt="Luxury notary desk with gold pen and legal documents"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[120%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/20" />
            <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
              <p className="font-serif italic text-ivory text-lg">Making your important<br />moments official.</p>
              <span className="eyebrow text-gold">Est. Chicago</span>
            </div>
          </div>
          <div className="absolute -top-6 -left-6 h-24 w-24 border border-gold/40 rounded-2xl bg-ink/40 backdrop-blur-sm grid place-items-center">
            <span className="font-serif gold-text text-3xl">MB</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
